/**
 * ParkingUtilizationReport Component
 *
 * Displays parking utilization data with a bar chart and table.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '@/lib/chartTheme';
import { useParkingUtilization } from '@/hooks/useParking';
import { formatDateTime } from '@/utils/locale';

export function ParkingUtilizationReport() {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  // Default to last 7 days
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const [startDate, setStartDate] = useState(weekAgo.toISOString().split('T')[0]!);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]!);

  const { data: report, isLoading } = useParkingUtilization(
    new Date(startDate).toISOString(),
    new Date(endDate + 'T23:59:59').toISOString()
  );

  const chartData = report?.zones.map((zone) => ({
    name: zone.zoneName,
    utilization: zone.utilizationPercent,
    vehicles: zone.vehicleCount,
  })) ?? [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('parkingUtilization.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('parkingUtilization.subtitle')}
      </Typography>

      <Stack direction="row" spacing={2} mb={3}>
        <TextField
          type="date"
          label={t('parkingUtilization.startDate')}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label={t('parkingUtilization.endDate')}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : !report || report.zones.length === 0 ? (
        <Alert severity="info">{t('parkingUtilization.noData')}</Alert>
      ) : (
        <>
          {/* Overall utilization + peak */}
          <Stack direction="row" spacing={2} mb={3} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2">{t('parkingUtilization.overall')}:</Typography>
            <Chip
              label={`${report.overall.utilizationPercent}%`}
              color={report.overall.utilizationPercent > 70 ? 'error' : report.overall.utilizationPercent > 40 ? 'warning' : 'success'}
            />
            {report.overall.peakOccupancy > 0 && (
              <>
                <Typography variant="subtitle2">{t('parkingUtilization.peakOccupancy')}:</Typography>
                <Chip
                  label={`${report.overall.peakOccupancy} / ${report.overall.totalSpots}`}
                  color="warning"
                  variant="outlined"
                />
                {report.overall.peakTime && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(report.overall.peakTime)}
                  </Typography>
                )}
              </>
            )}
          </Stack>

          {/* Bar chart */}
          <Box sx={{ width: '100%', height: 300, mb: 3 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} unit="%" />
                <Tooltip
                  {...chartTooltipProps(theme)}
                  formatter={(value: number) => [`${value}%`, t('parkingUtilization.utilization')]}
                />
                <Bar dataKey="utilization" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('parkingUtilization.zoneName')}</TableCell>
                  <TableCell align="right">{t('parkingUtilization.totalSpots')}</TableCell>
                  <TableCell align="right">{t('parkingUtilization.availableHours')}</TableCell>
                  <TableCell align="right">{t('parkingUtilization.usedHours')}</TableCell>
                  <TableCell align="right">{t('parkingUtilization.utilization')}</TableCell>
                  <TableCell align="right">{t('parkingUtilization.vehicleCount')}</TableCell>
                  <TableCell align="right">{t('parkingUtilization.peakOccupancy')}</TableCell>
                  <TableCell>{t('parkingUtilization.peakTime')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.zones.map((zone) => (
                  <TableRow key={zone.zoneId}>
                    <TableCell>{zone.zoneName}</TableCell>
                    <TableCell align="right">{zone.totalSpots}</TableCell>
                    <TableCell align="right">
                      {(zone.totalAvailableMinutes / 60).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      {(zone.totalUsedMinutes / 60).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={`${zone.utilizationPercent}%`}
                        color={zone.utilizationPercent > 70 ? 'error' : zone.utilizationPercent > 40 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="right">{zone.vehicleCount}</TableCell>
                    <TableCell align="right">{zone.peakOccupancy}</TableCell>
                    <TableCell>
                      {zone.peakTime ? (
                        <Typography variant="caption">
                          {formatDateTime(zone.peakTime)}
                        </Typography>
                      ) : '–'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Daily utilization trend chart */}
          {report.dailyUtilization.length > 1 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('parkingUtilization.trend')}
              </Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={report.dailyUtilization}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip
                      {...chartTooltipProps(theme)}
                      formatter={(value: number, name: string) => {
                        if (name === 'utilizationPercent') return [`${value}%`, t('parkingUtilization.utilization')];
                        return [`${value}`, t('parkingUtilization.avgOccupied')];
                      }}
                      labelFormatter={(label: string) => `${t('parkingUtilization.date')}: ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="utilizationPercent"
                      stroke={theme.palette.success.main}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
